/**
 * useThreadTimeline — THE data hook for an email thread's message history.
 *
 * Every thread surface (conversation body, participant derivation, header
 * reply actions, message count) previously called `useTimelineViewState`
 * itself, re-derived messages from raw events, and ran its own
 * page-until-exhausted effect — three identical effects racing the same
 * query. This hook consolidates all of that:
 *
 *  - One query definition (entity/filters), so every consumer shares one
 *    react-query cache entry.
 *  - One auto-paging code path. The full history is required by every
 *    consumer (the conversation renders it, participants/count derive from
 *    it), so pages are fetched until exhausted. `cancelRefetch: false`
 *    makes concurrent calls from multiple mounted instances no-ops against
 *    an in-flight fetch instead of cancel-restart churn.
 *  - Memoized derivations (`events`, `messages`, `lookups`) so consumers
 *    read results, not raw pages.
 *
 * Multiple mounted instances are cheap: the query is shared, and the
 * per-instance memos recompute only when the shared pages change.
 */

import { useEffect, useMemo } from 'react';

import {
  buildThreadMessages,
  THREAD_TIMELINE_FILTERS,
  type ThreadMessage,
} from './threadMessages';
import type { TimelineEvent, ViewStateLookups } from './types';
import { mergeLookups, useTimelineViewState } from './useTimelineViewState';

export interface UseThreadTimelineArgs {
  /** The thread entity id. Pass '' with enabled=false for non-thread hosts. */
  entityId: string;
  orgId: number | string;
  /** Gate the query entirely (e.g. speculative evaluation on non-threads). */
  enabled?: boolean;
}

export interface UseThreadTimelineResult {
  /** Raw email events across all fetched pages (newest-first, as the API returns). */
  events: TimelineEvent[];
  /** The conversation, oldest-first, one entry per logical email. */
  messages: ThreadMessage[];
  /** Merged view-state lookups (email → entity, entity → display fields). */
  lookups: ViewStateLookups | undefined;
  /** True during the initial fetch. */
  isLoading: boolean;
  /**
   * True while further pages remain or are in flight — derived data
   * (messages, counts, participants) is still incomplete.
   */
  isFetchingMore: boolean;
  error: unknown;
  refetch: () => void;
}

export function useThreadTimeline({
  entityId,
  orgId,
  enabled = true,
}: UseThreadTimelineArgs): UseThreadTimelineResult {
  const {
    data,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    error,
    refetch,
  } = useTimelineViewState({
    entityId,
    orgId: orgId.toString(),
    entityType: 'ahoy_email_thread',
    filters: THREAD_TIMELINE_FILTERS,
    enabled,
    payload: 'full',
  });

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      // cancelRefetch: false — when several thread surfaces are mounted,
      // their effects can fire in the same commit before the shared query
      // state flips to fetching; without this, later calls would CANCEL the
      // in-flight page request and restart it.
      fetchNextPage({ cancelRefetch: false });
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const events = useMemo(
    () => data?.pages.flatMap(page => page.events) ?? [],
    [data?.pages]
  );
  const messages = useMemo(() => buildThreadMessages(events), [events]);
  const lookups = useMemo(
    () => (data?.pages ? mergeLookups(data.pages) : undefined),
    [data?.pages]
  );

  return {
    events,
    messages,
    lookups,
    isLoading,
    isFetchingMore: Boolean(hasNextPage || isFetchingNextPage),
    error,
    refetch,
  };
}
